import { Typography } from 'antd';
import { Move, useListMovesQuery } from '../../redux/api/kit-staging';

interface Props {
	/** OLI to fetch movements for. Backend joins through both kit + material
	 * cards so the log shows the full history for the job's items. */
	oli: string;
	/** Cap shown; backend caps at 500 either way. */
	limit?: number;
}

const AREA_LABEL: Record<string, string> = {
	unassigned: 'Unassigned',
	cart: 'Cart',
	msa: 'MSA',
	jsa: 'JSA',
	dekit: 'Dekit',
	machine: 'Machine',
};

function formatArea(area: string): string {
	return AREA_LABEL[area] ?? area;
}

function formatTime(iso: string): string {
	try {
		const d = new Date(iso);
		return d.toLocaleString(undefined, {
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	} catch {
		return iso;
	}
}

function formatOperator(op: string): string {
	if (!op) return 'system';
	if (op.startsWith('system:')) return op.slice('system:'.length);
	return op;
}

export function CardMovementsLog({ oli, limit = 25 }: Props) {
	const { data, isLoading } = useListMovesQuery({ oli, limit }, { pollingInterval: 15000 });

	const moves = (data ?? []) as Move[];
	return (
		<details style={{ marginTop: 12 }}>
			<summary
				style={{
					cursor: 'pointer',
					fontSize: 11,
					color: '#8c8c8c',
					letterSpacing: 0.5,
					fontWeight: 700,
					userSelect: 'none',
				}}
			>
				MOVEMENTS{moves.length > 0 ? ` (${moves.length})` : ''}
			</summary>
			<div style={{ marginTop: 6 }}>
				{isLoading && moves.length === 0 ? (
					<Typography.Text type="secondary" style={{ fontSize: 11 }}>
						Loading…
					</Typography.Text>
				) : moves.length === 0 ? (
					<Typography.Text type="secondary" style={{ fontSize: 11 }}>
						No movements yet.
					</Typography.Text>
				) : (
					<ol
						style={{
							listStyle: 'none',
							padding: 0,
							margin: 0,
							display: 'flex',
							flexDirection: 'column',
							gap: 4,
							fontSize: 11,
							lineHeight: 1.4,
						}}
					>
						{moves.map((m) => {
							const isKit = m.card_item_type === 'kit';
							const badgeColor = isKit ? '#1890ff' : '#faad14';
							const badgeLabel = isKit ? 'KIT' : m.card_item_type === 'material' ? 'MAT' : '?';
							return (
								<li
									key={m.id}
									style={{
										borderLeft: `3px solid ${badgeColor}`,
										paddingLeft: 8,
										paddingTop: 2,
										paddingBottom: 2,
									}}
									title={`${m.action} (${m.rule})`}
								>
									<div style={{ color: '#595959', fontSize: 10 }}>
										{formatTime(m.created_at)} · {formatOperator(m.operator)}
									</div>
									<div style={{ color: '#262626', display: 'flex', alignItems: 'center', gap: 4 }}>
										<span
											style={{
												background: badgeColor,
												color: '#fff',
												fontSize: 9,
												fontWeight: 700,
												padding: '0 4px',
												borderRadius: 2,
											}}
										>
											{badgeLabel}
										</span>
										<span>
											{formatArea(m.from_area)} → {formatArea(m.to_area)}
										</span>
										<span style={{ color: '#8c8c8c' }}>[{m.action}]</span>
									</div>
								</li>
							);
						})}
					</ol>
				)}
			</div>
		</details>
	);
}
